'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { sendSignUpEvent } from 'analytics/events/recommended/signUp/signUp'
import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { BirthDateInput } from 'form-components/components/BirthDateInput/BirthDateInput'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { PasswordInput } from 'form-components/components/PasswordInput/PasswordInput'
import { PhoneInput } from 'form-components/components/PhoneInput/PhoneInput'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useRegistry } from 'registry/hooks/useRegistry'
import type {
	RegistryInputs,
	RegistryOptions,
} from 'registry/types/registryService'
import { useSession } from 'session/src/hooks/useSession'

import { GenericEvents } from '../../constants/analytics'
import {
	FormErrorTypes,
	FormFieldIds,
	FormFieldNames,
	TestIds,
	staticDefaultValues,
} from '../../constants/form'
import { PRIVACY_POLICY_CONFIGURATIONS } from '../../constants/privacyPolicyConfiguration'
import { RegistryOrigin } from '../../constants/registry'
import type { FormValues } from '../../types/form'
import { mapRegistryErrorsToFormErrors } from '../../utils/mapRegistryErrorsToFormErrors'
import { usePrivacyPolicyVariations } from '../../utils/privacyPolicyVariations/client/usePrivacyPolicyVariations'
import { GenericError } from './GenericError/GenericError'
import { NewsletterSubscriptionCheckbox } from './NewsletterSubscriptionCheckbox/NewsletterSubscriptionCheckbox'
import { PrivacyPolicy } from './PrivacyPolicy/PrivacyPolicy'

import styles from './Form.module.scss'

type RegistryFormProps = {
	readonly origin: RegistryOrigin
	readonly submitDescribedBy?: string
}

export function RegistryForm({
	origin,
	submitDescribedBy,
}: RegistryFormProps): React.ReactNode {
	const { pageType } = useMasterData()
	const { registry, registrySuccess, registryErrors } = useRegistry()
	const { t } = useLabels()
	const fieldsConfiguration = useFieldValidations()
	const { hashedEmail } = useSession()

	const variation = usePrivacyPolicyVariations()
	const showLegalCheck = variation === PRIVACY_POLICY_CONFIGURATIONS.VARIATION_2

	const defaultValues = {
		...staticDefaultValues,
		[FormFieldNames.NewsletterSubscription]:
			fieldsConfiguration.newsletter.value,
	}
	const formMethods = useForm({
		defaultValues,
		mode: 'onTouched',
		shouldFocusError: true,
	})
	const { handleSubmit, setError, reset, getValues } = formMethods

	const onSubmit = useCallback(async (values: FormValues): Promise<void> => {
		const {
			phone: { phoneCountryIso, phoneNumber, phonePrefix },
			...restOfValues
		} = values
		const registryInputs: RegistryInputs = {
			phone: {
				countryIso2: phoneCountryIso.split('-')[0],
				number: phoneNumber.toString(),
				prefix: phonePrefix,
			},
			...restOfValues,
		}
		const registryOptions: RegistryOptions = {
			origin: origin || RegistryOrigin.REGISTRY,
		}
		await registry(registryInputs, registryOptions)
	}, [])

	const resetValues = useCallback(() => reset(defaultValues), [])
	useEffect(() => {
		sendGenericEvent(GenericEvents.REGISTRY_SHOW)
	}, [])

	useEffect(() => {
		if (registryErrors) {
			mapRegistryErrorsToFormErrors(registryErrors, {
				fieldsConfiguration,
			}).forEach((error) => {
				if (error) {
					const { field, messageKey } = error

					sendGenericEvent(`registry_post_validation_${field}`)
					setError(
						field,
						{
							type: FormErrorTypes.Validate,
							message: messageKey && t(messageKey),
						},
						{ shouldFocus: true }
					)
				}
			})
		}
	}, [registryErrors])

	useEffect(() => {
		if (registrySuccess && hashedEmail) {
			const newsletterSubscriptionChecked = getValues('newsletterSubscription')

			if (newsletterSubscriptionChecked) {
				const newsletterRegistryPayload = {
					method: 'native',
					newsletter_subscription: '1',
					location: pageType,
				}
				sendGenericEvent(
					GenericEvents.NEWSLETTER_SUBSCRIBE,
					newsletterRegistryPayload
				)
			}

			const registrySuccessPayload = {
				sign_ups: 1,
				email: hashedEmail,
			}

			sendSignUpEvent(registrySuccessPayload)
		}
	}, [registrySuccess, hashedEmail])

	return (
		<FormProvider {...formMethods}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<EmailInput
					name={FormFieldNames.Email}
					id={FormFieldIds.Email}
					rules={fieldsConfiguration.email}
					data-testid={TestIds.EmailInput}
					errorMessageDataTestId={TestIds.EmailInputErrorMessage}
				/>
				<PasswordInput
					name={FormFieldNames.Password}
					id={FormFieldIds.Password}
					rules={fieldsConfiguration.password}
					data-testid={TestIds.PasswordInput}
					hintMessage={t('form.password.helpmessage')}
					errorMessageDataTestId={TestIds.PasswordInputErrorMessage}
				/>
				<PhoneInput
					name={FormFieldNames.Phone}
					id={FormFieldIds.Phone}
					rules={fieldsConfiguration.mobilePhone}
					data-testids={{
						phoneNumber: TestIds.PhoneNumber,
						countryPrefix: TestIds.PhoneCountryPrefix,
						selectPrefix: TestIds.PhoneSelectPrefix,
					}}
				/>
				{showLegalCheck && (
					<BirthDateInput
						name={FormFieldNames.BirthDate}
						id={FormFieldIds.BirthDate}
						data-testid={TestIds.BirthDate}
						rules={{ ...fieldsConfiguration.birthDate, required: true }}
						errorMessage={t('form.birthday.below18.errorMessage')}
						errorMessageDataTestId={TestIds.BirthDateErrorMessage}
					/>
				)}
				<PrivacyPolicy origin={origin} />
				<NewsletterSubscriptionCheckbox
					origin={origin}
					id={FormFieldIds.NewsletterSubscription}
					name={FormFieldNames.NewsletterSubscription}
					data-testid={TestIds.NewsletterSubscription}
				/>
				<GenericError
					submitHandler={onSubmit}
					resetValues={resetValues}
					origin={origin}
				/>
				<SubmitButton
					id={FormFieldIds.RegistryButton}
					data-testid={TestIds.RegistryButton}
					aria-describedby={submitDescribedBy}
				>
					{t('createAccount.createAccount.button')}
				</SubmitButton>
			</form>
		</FormProvider>
	)
}
